import {memo} from 'react';
import {Avatar, Box, Stack, Typography} from '@mui/material';
import {StyledTooltip} from 'components/StyledTooltip/StyledTooltip';
import {Handle, Position, NodeProps} from 'reactflow';
import {phaseColorsConstants} from 'app/types/Shared';
import {UserFilled, WarningAltFilled} from '@carbon/icons-react';
import {useSelector} from 'react-redux';
import {selectRoleFilters} from 'app/store/appState/appStateSlice';
import theme from 'themes';

function GroupNode({data, id}: NodeProps) {
  type groupRoles = {
    raci_list: string;
    role: {
      abbrev: string;
      role_category: {
        abbrev: string;
        name: string;
        label: string;
      };
    };
  };
  const transformRoles = (roles: Array<groupRoles>) => {
    const responsibleRoles: Array<string> = [];
    const accountableRoles: Array<string> = [];
    roles.forEach((role) => {
      if (role.raci_list.includes('R')) {
        if (!responsibleRoles.includes(role.role.role_category.abbrev)) {
          responsibleRoles.push(role.role.role_category.abbrev);
        }
      }
      if (role.raci_list.includes('A')) {
        if (!accountableRoles.includes(role.role.role_category.abbrev))
          accountableRoles.push(role.role.role_category.abbrev);
      }
    });
    const responsible =
      responsibleRoles.length != 0 ? (
        <Stack direction='row' alignItems='center' spacing={1}>
          <Avatar sx={{width: 20, height: 20, bgcolor: '#ABB436'}} color='success'>
            <Typography variant='subtitle2' color='grey.50' sx={{fontWeight: 'bold'}}>
              R
            </Typography>
          </Avatar>
          {responsibleRoles.length > 5 ? (
            <StyledTooltip
              placement='bottom-start'
              title={
                <Box p={0.2} maxWidth={200}>
                  <Typography color={theme().palette.grey[700]} variant='h5'>
                    {responsibleRoles.join(', ')}
                  </Typography>
                </Box>
              }
            >
              <Typography variant='h5'>{`${responsibleRoles.slice(0, 5).join(', ')}${
                responsibleRoles.length > 5 && ', ...'
              }`}</Typography>
            </StyledTooltip>
          ) : (
            <Typography variant='h5'>{responsibleRoles.join(', ')}</Typography>
          )}
        </Stack>
      ) : (
        <></>
      );
    const accountable =
      accountableRoles.length != 0 ? (
        <Stack direction='row' alignItems='center' spacing={1}>
          <Avatar sx={{width: 20, height: 20, bgcolor: '#EA5532'}} color='success'>
            <Typography variant='subtitle2' color='grey.50' sx={{fontWeight: 'bold'}}>
              A
            </Typography>
          </Avatar>
          {accountableRoles.length > 5 ? (
            <StyledTooltip
              placement='bottom'
              title={
                <Box p={0.2} maxWidth={200}>
                  <Typography color={theme().palette.grey[700]} variant='h5'>
                    {accountableRoles.join(', ')}
                  </Typography>
                </Box>
              }
            >
              <Typography variant='h5'>{`${accountableRoles.slice(0, 5).join(', ')}${
                accountableRoles.length > 5 && ', ...'
              }`}</Typography>
            </StyledTooltip>
          ) : (
            <Typography variant='h5'>{accountableRoles.join(', ')}</Typography>
          )}
        </Stack>
      ) : (
        <></>
      );
    return (
      <Stack spacing={1} direction='row'>
        {responsible}
        {accountable}
      </Stack>
    );
  };

  const selectedRoles = useSelector(selectRoleFilters);
  const hasRole = (roles: Array<groupRoles>) => {
    let flag = false;
    roles.map((r) => {
      if (selectedRoles.some((role) => role === r.role.role_category.label)) {
        flag = true;
      }
    });
    return flag;
  };
  const person = hasRole(data.roles) ? (
    <Box minWidth='18px' minHeight='18px' display='flex' alignItems='center' justifyContent='center'>
      <UserFilled width='18px' height='18px' style={{color: 'grey.800'}} />
    </Box>
  ) : (
    <></>
  );
  const width = data.width;
  const height = data.height + 40;
  const handleStyle = {opacity: '0'};
  const labelId = data.label ? data.label.indexOf(' ') : -1;
  const label = labelId === -1 ? '' : data.label.substring(labelId);
  const phases: Array<React.ReactNode> = [];
  data.phases.forEach((p: string) => {
    const phase = phaseColorsConstants.find((x) => x.name == p);
    if (phase) {
      phases.push(
        <Box sx={{backgroundColor: phase.color, borderRadius: 1, padding: '2px 4px'}} key={phase.name}>
          <Typography sx={{fontSize: '0.75rem', color: 'white', fontWeight: 'bold'}}>{phase.name}</Typography>
        </Box>,
      );
    }
  });
  const update = data.update ? <WarningAltFilled fontSize='medium' /> : <></>;
  return (
    <div style={{width, height}}>
      <Box
        id={`${id}-group-node`}
        sx={{
          width: data.width,
          display: 'flex',
          alignItems: 'center',
          padding: 1,
        }}
      >
        <Stack direction='column' px={5} spacing={1} sx={{maxWidth: 280, maxHeight: 175}}>
          <Stack direction='row' spacing={1} justifyContent='flex-start' alignItems='center' minHeight={'24px'}>
            {phases}
            {person}
            {update}
          </Stack>
          <Typography variant='h2'>{data.id}</Typography>
          <Typography variant='h3'>{label}</Typography>
          {transformRoles(data.roles)}
        </Stack>
        <Handle id='left' style={handleStyle} type='target' position={Position.Left} />
        <Handle id='top' style={handleStyle} type='target' position={Position.Top} />
        <Handle id='right' style={handleStyle} type='source' position={Position.Right} />
        <Handle id='bottom' style={handleStyle} type='source' position={Position.Bottom} />
      </Box>
    </div>
  );
}

export default memo(GroupNode);
